import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import 'rc-slider/assets/index.css';
import styles from './DeviceClusteringChart.module.css';
import reduceData from './reduceData';
import RangeSlider from './RangeSlider';

export default function DeviceClusteringChart({chartData,step,onChange}){
  const chartRef = useRef(null);
  const [startValue, setStartValue] = useState(0);
  const [endValue, setEndValue] = useState(0);
  const [data,setData]=useState(null);
  const [marks,setMarks] = useState(null);
  const [stepValueLoading,setStepValueLoading] = useState(false);
  const [reducedData,setReducedData]=useState(null);
  const [nowDataSize,setNowDataSize]=useState(null);
  const [nowDeviceSize,setNowDeviceSize]=useState(null);

  const [chartWidth,setChartWidth]=useState(chartRef.current?.clientWidth||700);
  const [chartHeight,setChartHeight]=useState(chartRef.current?.clientHeight||300);
  const labelWidth=80;
  const labelPad=14;

  const isFirstRender=useRef(true);

  useEffect(()=>{
    onChange(nowDeviceSize,nowDataSize);
  },[nowDeviceSize,nowDataSize])

  useEffect(()=>{
    setChartWidth(chartRef.current?.clientWidth||700);
    setChartHeight(chartRef.current?.clientHeight||300);
  },[chartRef])

  useEffect(()=>{
    setData(()=>{
      const newData=[];
      for (const key in chartData) {
        const newDataItem={
        x : {max:parseInt(key),min:parseInt(key)},
        y : chartData[key]
        }
        newData.push(newDataItem);
      }
      return newData;
    });
  },[chartData])

  useEffect(()=>{
    if(data===null||data.length===0)return;
    setNowDataSize(data[0].x.min);
    setNowDeviceSize(data[0].y);
    if(data.length==1){
      setReducedData([{x:{min:data[0].x.min,max:data[0].x.max},y:data[0].y}]);
    }
    else{
      setReducedData(reduceData(data,step));
    }
  },[data,step])

  useEffect(()=>{
    if(reducedData==null || reducedData.length==0)return;
    setMarks(()=>{
      const newData={};
      reducedData.forEach((item,index) => {
        if(item.x.min==item.x.max){
          newData[index] = item.x.min;
        }
        else{
          newData[index] = item.x.min+" ~ "+item.x.max;
        }
      });
      return newData;
    })
    setStartValue(0);
    setEndValue(reducedData.length-1)
    setStepValueLoading(true);
    drawChart();
  },[reducedData,chartWidth,chartHeight])

  useEffect(() => {
    if(reducedData==null || reducedData.length==0)return;
    drawChart();
    setNowDataSize(reducedData[startValue].x.min+" ~ "+reducedData[endValue].x.max);
    setNowDeviceSize(()=>{
      let nowSize=0;
      for(let i=startValue;i<=endValue;i++){
        nowSize+=reducedData[i].y;
      }
      return nowSize;
    });
  }, [startValue, endValue,chartWidth,chartHeight]);

  useEffect(()=>{
    if(!reducedData||reducedData.length===0)return;
    setNowDeviceSize(reducedData[0].y)
  },[reducedData])

  const drawChart = () => {
    d3.select(chartRef.current).selectAll('*').remove();

    setChartWidth(chartRef.current?.clientWidth||700);
    setChartHeight(chartRef.current?.clientHeight||300);

    const svg = d3
    .select(chartRef.current)
    .append('svg')
    .attr('width', chartWidth+labelWidth)
    .attr('height', chartHeight+1)
    .append('g')
    // .attr('transform', `translate(${200},${0})`);

    let minY = d3.min(reducedData, d => d.y);
    let maxY = d3.max(reducedData, d => d.y);

    if(minY===maxY){
      if(minY<=1){
        minY=1;
        maxY=10;
      }else{
        minY=minY/2
        maxY=maxY*2
      }
    }

    const y = d3
    .scaleLog()
    .domain([.5, maxY])
    .range([chartHeight, 0])
    .base(10)
    .clamp(true);

    const allTicks=[];
    for (let exp = 0; Math.pow(10,exp) <= maxY; exp++) {
      const tick=Math.pow(10,exp);
      if(tick>=minY&&tick<=maxY){
        allTicks.push(tick)
      }
    }

    let tickValues=[0];
    if(allTicks.length>2){
      const midIndex=Math.floor(allTicks.length/2);
      tickValues.push(allTicks[midIndex])
    }
    tickValues.push(maxY);
    tickValues=Array.from(new Set(tickValues)).sort((a,b)=>a-b);

    const yAxis = d3
    .axisRight(y)
    .tickSize(-chartWidth)
    .tickValues(tickValues)
    .tickPadding(6)
    .tickFormat(d3.format(",.0f"))

    const yAxisGroup = svg.append('g').attr('transform',`translate(${chartWidth+labelPad},0)`).call(yAxis);
    yAxisGroup.select('.domain')
    .attr('stroke', 'none');
    yAxisGroup.select('.domain').remove();
    yAxisGroup.selectAll('.tick > line')
    .attr('stroke', '#D5D5D5D5')
    .attr('stroke-dasharray', '6,10');
    yAxisGroup.selectAll('text')
    .attr('fill', '#ABABAB')
    .attr('font-size', '12px')
    .attr('font-family', 'SamsungOne')
    .attr('font-weight', '800');
    yAxisGroup.selectAll('text')
    .filter((d,i)=>i===0)
    .attr('dy','-0.3rem')
    yAxisGroup.selectAll('text')
    .filter((d,i,nodes)=>i===nodes.length-1)
    .attr('dy','1.4rem')

    const defs=svg.append('defs');
    const gradient=defs.append('linearGradient')
    .attr('id','barGradient')
    .attr('x1','20%')
    .attr('y1','0%')
    .attr('x2','40%')
    .attr('y2','100%')

    gradient.append('stop')
    .attr('offset','20%')
    .attr('stop-color','#181818')

    gradient.append('stop')
    .attr('offset','100%')
    .attr('stop-color','#aaa')

    // const barWidth=width/reducedData.length<20?width/reducedData.length:20;
    const barWidth=20;
    const barGap=chartWidth/reducedData.length-barWidth;

    const totalBars=reducedData.length;
    // const barWidth=(width-barGap*(totalBars-1))/totalBars;
    const totalBarWidth=barWidth*totalBars+barGap*(totalBars-1);
    const barStartX=(chartWidth-totalBarWidth)/2;

    svg.selectAll('.bar')
    .data(reducedData)
    .enter()
    .append('rect')
    .attr('class','bar')
    .attr('x',(d,i)=>barStartX+i*(barWidth+barGap))
    .attr('width',barWidth)
    .attr('rx',barWidth/2.6)
    .attr('ry',barWidth/2.6)
    .attr('fill',(d,i)=>(i>=startValue && i <=endValue?'url(#barGradient)':'#f4f5fb'))
    .attr('stroke',(d,i)=>(i>=startValue && i <=endValue?'#181818':'#181818'))
    .attr('stroke-width',(d,i)=>(i>=startValue && i <=endValue?1:1.5))
    .attr('stroke-dasharray',(d,i)=>(i>=startValue && i <=endValue?'none':'4,4'))

    if(isFirstRender.current){
      svg.selectAll('.bar')
      .attr('y',chartHeight)
      .attr('height',0)
      .transition()
      .duration(180)
      .delay((_,i)=>i*30)
      .attr('y',d=>y(d.y))
      .attr('height',d=>chartHeight-y(d.y))
    }else{
      svg.selectAll('.bar')
      .attr('y',d=>y(d.y))
      .attr('height',d=>chartHeight-y(d.y))
    }

    const tooltip=d3.select('body').append('div')
    .attr('class',styles.tooltip)

    svg.selectAll('.bar')
    .on('mouseover',function(e,d){
      tooltip
      .style('opacity',1)
      .html(`
        Device: ${d.y}
        <br/>
        <br/>
        Data: ${d.x.min} ~ ${d.x.max} 
      `)
    })
    .on('mousemove',function(e){
      tooltip
      .style('left',`${e.pageX + 10}px`)
      .style('top',`${e.pageY + 28}px`)
    })
    .on('mouseout',function(){
      tooltip.style('opacity',0)
    })

  };

  return (
    <>
    {data &&
    <div className={styles['chart-wrap']}>
      <div ref={chartRef} className={styles['chart']}></div>
      <div className={styles['slider']}>
        <RangeSlider
        reducedData={reducedData}
        startValue={startValue}
        endValue={endValue}
        onChange={([start,end])=>{
          isFirstRender.current=false;
          setStartValue(start);
          setEndValue(end);
        }}
        chartWidth={chartWidth}
        />
      </div>
    </div>
    }
    </>
  );
};